/**
 * 표 폭 — 저장값의 뜻과 그리는 법의 **정본**. 편집기 화면(TableWidthPlugin)과 인쇄·시험지로 가는
 * HTML(tableWidthLexical 의 내보내기 덧씌움)이 이 파일 하나를 함께 쓴다.
 *
 * ── 왜 비율인가 (2026-10-06 오너 결정: 「좁히기 + 종이까지 맞춤」) ─────────────────
 * 칸 폭은 Lexical 이 `width`(px)로 저장한다. 그런데 같은 표가 편집기(423px)·시험지 미리보기(423px)·
 * A4 인쇄 칸(281.55px)에 그려진다. px 그대로 그리면 종이에서만 표가 1.5배 넓어지고, 테마의
 * `width: 100%` 를 그대로 두면 화면에서 줄인 폭이 매번 되돌아간다(실측: 세 열을 모두 끈 뒤 3열 −40 →
 * 기대 160.7, 실제 177.5). 그래서 **저장값은 기준 폭 423 위의 px 로 읽고, 그릴 때는 비율로 바꾼다.**
 *   표 폭 = min(첫 줄 칸 폭의 합, 423) / 423  ← 담는 칸 폭에 대한 %
 *   칸 폭 = 칸 폭 / 첫 줄 칸 폭의 합           ← 표 폭에 대한 %
 * 실측(앱 CSS 안): 기준 [100,60,80] → 423 칸 240px(56.7%) · 281.55 칸 159.7px(56.7%), 칸 비율 그대로.
 *
 * ── 언제 이 규칙을 쓰나 ───────────────────────────────────────────────────
 * **첫 줄의 모든 칸에 폭이 있는 표만**(「크기가 정해진 표」). 한 번도 끌지 않은 표(OCR 표 대부분)와
 * 일부 열만 끈 옛 표는 손대지 않는다 — 화면·종이 모두 종전과 같아야 한다.
 * 손잡이(TableCellResizer)는 처음 끌 때 모든 열의 지금 폭을 한꺼번에 저장해 이 상태로 만든다.
 *
 * ── 배치 방식 ─────────────────────────────────────────────────────────────
 * 크기가 정해진 표는 `table-layout: auto` 로 그린다. 테마의 fixed 는 **첫 줄만** 보므로 첫 줄에 합친
 * 칸이 있으면 그 아래 열을 따로 맞출 수 없다(실측: 기대 60/140/100 → auto 59.8/139.5/99.7).
 * 편집기 테마의 칸 최소 폭 75px 은 auto 에서 살아나 비율을 흔들므로 같이 0 으로 푼다
 * (실측: 기대 100/60/80 → 최소 폭이 살아 있으면 86.8/75/77.2).
 */

/** 저장된 칸 폭을 읽는 기준 폭(px). 문제 보기 칸·시험지 미리보기의 본문 폭 PAPER_BODY_PX 와 같은 값이다(테스트가 지킨다) */
export const TABLE_REF_WIDTH_PX = 423;

/** 손잡이로 줄일 수 있는 가장 좁은 열(기준 폭 위 px). 종전 손잡이의 최소 폭을 그대로 쓴다 */
export const MIN_COLUMN_REF_PX = 50;

/** 합이 기준 폭에서 이만큼 안쪽이면 꽉 찬 표로 본다 — 접힌 테두리 반 픽셀 때문에 꽉 찬 표가 422.1 로 재진다(실측) */
const FULL_WIDTH_SNAP_PX = 2;

export type CellSpan = { colSpan: number; rowSpan: number };
export type SizedCell = CellSpan & { width?: number | null };
export type TableGrid = { starts: number[][]; colCount: number };

export type TableWidthPlan =
  | { sized: false }
  | { sized: true; tablePercent: number; cellPercents: (number | null)[][] };

const spanOf = (n: number | undefined) =>
  Number.isFinite(n) && (n as number) > 1 ? Math.floor(n as number) : 1;

/** 쓸 수 있는 칸 폭인가 — 0·음수·NaN·null 은 「폭 없음」이다(Lexical 은 폭 없는 칸을 undefined 로 둔다) */
export const isCellWidth = (w: unknown): w is number =>
  typeof w === 'number' && Number.isFinite(w) && w > 0;

const sum = (a: number[]) => a.reduce((s, v) => s + v, 0);

/**
 * 칸 지도 — rows[r][i] 칸이 몇 번째 **열**에서 시작하는가.
 * 칸의 순서(자식 번호)와 열 번호는 합친 칸이 있으면 다르다. 종전 손잡이는 자식 번호로 폭을 바꿔
 * 합친 표에서 엉뚱한 열이 바뀌었다(실측: a열만 −40 했는데 a·b 둘 다 50.3).
 */
export function mapTableGrid(rows: CellSpan[][]): TableGrid {
  const occupied: boolean[][] = rows.map(() => []);
  const starts: number[][] = rows.map(() => []);
  let colCount = 0;
  rows.forEach((cells, r) => {
    let c = 0;
    cells.forEach((cell, i) => {
      while (occupied[r][c]) c += 1;
      starts[r][i] = c;
      const cs = spanOf(cell.colSpan);
      const rs = spanOf(cell.rowSpan);
      for (let dr = 0; dr < rs && r + dr < rows.length; dr += 1) {
        for (let dc = 0; dc < cs; dc += 1) occupied[r + dr][c + dc] = true;
      }
      c += cs;
      colCount = Math.max(colCount, c);
    });
  });
  return { starts, colCount };
}

/** 첫 줄의 모든 칸에 폭이 있는가 — 이 규칙(비율로 그리기)을 쓸 표인가 */
export function isSizedTable(rows: SizedCell[][]): boolean {
  const first = rows[0];
  return !!first && first.length > 0 && first.every((c) => isCellWidth(c.width));
}

/** 표 전체 폭(기준 폭 위 px) = 첫 줄 칸 폭의 합. 합친 칸의 폭은 덮는 열의 합으로 저장된다 */
export function tableTotalWidth(rows: SizedCell[][]): number {
  return sum((rows[0] || []).map((c) => (isCellWidth(c.width) ? c.width : 0)));
}

/** 열마다 한 칸짜리 칸의 폭(맨 위 것). 그런 칸이 없는 열은 null */
function singleCellWidths(rows: SizedCell[][], grid: TableGrid): (number | null)[] {
  const cols: (number | null)[] = Array(grid.colCount).fill(null);
  rows.forEach((cells, r) => cells.forEach((cell, i) => {
    const c = grid.starts[r][i];
    if (spanOf(cell.colSpan) === 1 && isCellWidth(cell.width) && cols[c] == null) cols[c] = cell.width;
  }));
  return cols;
}

/** 모든 열에 한 칸짜리 칸 폭이 있을 때만 그 열 폭들. 하나라도 합친 칸만 덮으면 null */
export function singleCellColumnWidths(rows: SizedCell[][], grid: TableGrid = mapTableGrid(rows)): number[] | null {
  const cols = singleCellWidths(rows, grid);
  return cols.length > 0 && cols.every((w) => w != null) ? (cols as number[]) : null;
}

/**
 * 칸 폭(px 이든 기준 폭이든)에서 열 폭을 어림한다. 한 칸짜리 칸이 정본이고, 합친 칸만 덮는 열은
 * 합친 칸 폭에서 아는 열을 뺀 나머지를 똑같이 나눈다(다른 줄의 경계는 보지 않는 어림이다 — 손잡이는
 * 이 값을 쓰지 않고 columnWidthsFromEdges 로 잰다). 끝내 모르는 열이 남으면 null.
 */
export function columnWidthsFromCells(rows: SizedCell[][], grid: TableGrid = mapTableGrid(rows)): number[] | null {
  const cols = singleCellWidths(rows, grid);
  for (let changed = true; changed;) {
    changed = false;
    rows.forEach((cells, r) => cells.forEach((cell, i) => {
      const cs = spanOf(cell.colSpan);
      if (cs === 1 || !isCellWidth(cell.width)) return;
      const range = Array.from({ length: cs }, (_, k) => grid.starts[r][i] + k).filter((c) => c < grid.colCount);
      const unknown = range.filter((c) => cols[c] == null);
      if (unknown.length === 0) return;
      const rest = cell.width - sum(range.filter((c) => cols[c] != null).map((c) => cols[c] as number));
      if (rest <= 0) return;
      unknown.forEach((c) => { cols[c] = rest / unknown.length; });
      changed = true;
    }));
  }
  return cols.every((w) => w != null) ? (cols as number[]) : null;
}

export type CellEdges = { left: number; right: number } | null;

/**
 * 그려진 칸의 좌우 경계(px)에서 열 폭을 잰다. 칸마다 시작 열의 왼쪽 선과 끝 열의 오른쪽 선을 알려 준다.
 * 어느 칸도 지나지 않는 선(합친 칸 속에 숨은 선)은 화면에 없으므로 양옆의 아는 선 사이를 고르게 나눈다.
 * 칸 폭 합으로 어림하면(columnWidthsFromCells) 엇갈려 합친 표에서 건드리지 않은 칸까지 폭이 바뀐다
 * (2차 검토 지적 2026-10-06: 첫 줄 3칸씩·둘째 줄 2칸씩 합친 6열 표 → 마지막 칸 120 → 140).
 */
export function columnWidthsFromEdges(rows: CellSpan[][], grid: TableGrid, edges: CellEdges[][]): number[] | null {
  const n = grid.colCount;
  if (n === 0) return null;
  const total: number[] = Array(n + 1).fill(0);
  const count: number[] = Array(n + 1).fill(0);
  rows.forEach((cells, r) => cells.forEach((cell, i) => {
    const e = edges[r]?.[i];
    if (!e || !Number.isFinite(e.left) || !Number.isFinite(e.right)) return;
    const start = grid.starts[r][i];
    const end = Math.min(start + spanOf(cell.colSpan), n);
    total[start] += e.left;
    count[start] += 1;
    total[end] += e.right;
    count[end] += 1;
  }));
  const xs: (number | null)[] = total.map((v, k) => (count[k] ? v / count[k] : null));
  if (xs[0] == null || xs[n] == null) return null;
  for (let k = 1; k < n; k += 1) {
    if (xs[k] != null) continue;
    let j = k + 1;
    while (xs[j] == null) j += 1;
    xs[k] = (xs[k - 1] as number) + ((xs[j] as number) - (xs[k - 1] as number)) / (j - k + 1);
  }
  const cols = Array.from({ length: n }, (_, k) => (xs[k + 1] as number) - (xs[k] as number));
  return cols.every((w) => w > 0) ? cols : null;
}

/** 열 폭을 칸마다 나눠 준다 — 한 칸짜리는 그 열 폭, 합친 칸은 덮는 열의 합 */
export function cellWidthsFromColumns(rows: CellSpan[][], grid: TableGrid, cols: number[]): number[][] {
  return rows.map((cells, r) => cells.map((cell, i) => {
    const start = grid.starts[r][i];
    return sum(cols.slice(start, start + spanOf(cell.colSpan)));
  }));
}

/**
 * 열 폭을 기준 폭 안으로. 합이 기준을 넘으면(옛 표·붙여넣은 표) 같은 비율로 줄이고, 거의 꽉 찼으면
 * 정확히 꽉 채운다 — 그려질 때도 넘는 표는 100% 로 서므로 보이는 모양은 바뀌지 않는다.
 */
export function fitColumnsToRef(cols: number[]): number[] {
  const total = sum(cols);
  if (!(total > 0)) return cols.slice();
  if (total > TABLE_REF_WIDTH_PX || TABLE_REF_WIDTH_PX - total <= FULL_WIDTH_SNAP_PX) {
    return cols.map((w) => (w * TABLE_REF_WIDTH_PX) / total);
  }
  return cols.slice();
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/**
 * 경계선 끌기 (2026-10-06 오너 결정).
 *   안쪽 선(boundary 열의 오른쪽 선, 마지막 열이 아님) — **양옆 두 열만** 바뀌고 표 폭은 그대로(한글·워드 방식).
 *   바깥 선(마지막 열의 오른쪽 선) — 마지막 열이 바뀌어 **표 폭이 바뀐다**. 기준 폭(100%)을 넘지 않는다.
 * 이미 최소 폭보다 좁은 열(옛 표)은 지금 폭을 하한으로 삼는다 — 끄는 방향과 반대로 튀지 않게.
 */
export function resizeColumns(cols: number[], boundary: number, delta: number, min = MIN_COLUMN_REF_PX): number[] {
  const next = cols.slice();
  const n = next.length;
  if (!(boundary >= 0 && boundary < n) || !Number.isFinite(delta)) return next;
  if (boundary < n - 1) {
    const pair = next[boundary] + next[boundary + 1];
    const lo = Math.min(min, next[boundary]);
    const hi = pair - Math.min(min, next[boundary + 1]);
    if (hi < lo) return next;
    next[boundary] = clamp(next[boundary] + delta, lo, hi);
    next[boundary + 1] = pair - next[boundary];
  } else {
    const others = sum(next.slice(0, n - 1));
    const lo = Math.min(min, next[boundary]);
    const hi = Math.max(TABLE_REF_WIDTH_PX - others, lo);
    next[boundary] = clamp(next[boundary] + delta, lo, hi);
  }
  return next;
}

/** 저장할 때 소수 둘째 자리까지 — JSON 이 81.1875 같은 꼬리로 지저분해지지 않게 */
export const roundWidth = (w: number) => Math.round(w * 100) / 100;

/**
 * 손잡이 한 번 → 칸마다 새 폭(기준 폭 위, 줄·칸 순서는 rows 와 같다). 계산할 수 없으면 null — 손잡이는 그만둔다.
 *   active          끈 칸의 위치(rows[row][index]). 그 칸의 오른쪽 선 = 끝 열의 오른쪽 선을 옮긴다
 *   edges           지금 그려진 칸의 좌우 경계(px)
 *   containerWidth  표를 담은 칸의 글자 폭(px) — 표의 % 가 이것에 대한 비율이다
 * 열 폭은 크기가 정해진 표이고 모든 열에 한 칸짜리 칸 폭이 있으면 **저장값**을 쓴다 — 그려진 폭을 다시 재면
 * 접힌 테두리 반 픽셀(실측 표 240 → 칸 합 239.1)이 끌 때마다 쌓인다. 그 밖에는 그려진 경계를 잰다.
 */
export function resizeTableCells(params: {
  rows: SizedCell[][];
  edges: CellEdges[][];
  containerWidth: number;
  active: { row: number; index: number };
  deltaPx: number;
  align?: TableAlign;
}): number[][] | null {
  const { rows, edges, containerWidth, active, deltaPx, align = 'left' } = params;
  if (!(containerWidth > 0) || !Number.isFinite(deltaPx)) return null;
  const grid = mapTableGrid(rows);
  const start = grid.starts[active.row]?.[active.index];
  if (start == null) return null;
  const boundary = Math.min(start + spanOf(rows[active.row][active.index].colSpan), grid.colCount) - 1;

  let cols = isSizedTable(rows) ? singleCellColumnWidths(rows, grid) : null;
  if (!cols) {
    const px = columnWidthsFromEdges(rows, grid, edges);
    if (!px) return null;
    cols = px.map((w) => (w * TABLE_REF_WIDTH_PX) / containerWidth);
  }
  // 가운데 표는 폭이 바뀌면 양쪽이 반씩 움직인다 — 바깥 선이 마우스를 따라오게 두 배로 바꾼다
  const scale = align === 'center' && boundary === grid.colCount - 1 ? 2 : 1;
  const next = resizeColumns(fitColumnsToRef(cols), boundary, (deltaPx * scale * TABLE_REF_WIDTH_PX) / containerWidth);
  return cellWidthsFromColumns(rows, grid, next).map((cells) => cells.map(roundWidth));
}

/** 그리기 계획 — 크기가 정해진 표만 비율을 낸다. 나머지는 { sized: false }(손대지 않음) */
export function planTableWidths(rows: SizedCell[][]): TableWidthPlan {
  if (!isSizedTable(rows)) return { sized: false };
  const total = tableTotalWidth(rows);
  return {
    sized: true,
    tablePercent: (Math.min(total, TABLE_REF_WIDTH_PX) / TABLE_REF_WIDTH_PX) * 100,
    cellPercents: rows.map((cells) => cells.map((c) => (isCellWidth(c.width) ? (c.width / total) * 100 : null))),
  };
}

const percent = (p: number) => `${Number(p.toFixed(4))}%`;

type StyleProp = 'width' | 'minWidth' | 'tableLayout' | 'marginLeft' | 'marginRight';
const CSS_NAME: Record<StyleProp, string> = {
  width: 'width',
  minWidth: 'min-width',
  tableLayout: 'table-layout',
  marginLeft: 'margin-left',
  marginRight: 'margin-right',
};

/** 같은 값이면 쓰지 않는다. 빈 값은 속성을 지운다 — `style.marginLeft = ''` 가 안 먹는 환경(jsdom)이 있다 */
const setStyle = (el: HTMLElement, prop: StyleProp, value: string) => {
  if (el.style[prop] === value) return;
  if (value === '') el.style.removeProperty(CSS_NAME[prop]);
  else el.style[prop] = value;
};

/**
 * 계획을 표 DOM 에 입힌다. cellEls[r][i] 는 rows[r][i] 의 DOM 이어야 한다(없으면 null — 건너뛴다).
 * 크기가 정해지지 않은 표는 restoreWidths(Lexical 이 그렸을 px)로 되돌린다 — 열을 넣어 폭 없는 칸이
 * 생긴 표가 비율 스타일을 붙든 채 남지 않게. 내보내기는 restoreWidths 를 주지 않는다(애초에 손대지 않는다).
 */
export function applyTableWidthPlan(
  tableEl: HTMLElement,
  cellEls: (HTMLElement | null)[][],
  plan: TableWidthPlan,
  restoreWidths?: (number | null | undefined)[][],
): void {
  if (plan.sized) {
    setStyle(tableEl, 'width', percent(plan.tablePercent));
    setStyle(tableEl, 'tableLayout', 'auto');
    cellEls.forEach((row, r) => row.forEach((el, i) => {
      if (!el) return;
      const p = plan.cellPercents[r]?.[i];
      setStyle(el, 'width', p == null ? '' : percent(p));
      setStyle(el, 'minWidth', '0px'); // 브라우저가 '0' 을 '0px' 로 돌려준다 — 같은 값을 다시 쓰지 않게
    }));
    return;
  }
  if (!restoreWidths) return;
  setStyle(tableEl, 'width', '');
  setStyle(tableEl, 'tableLayout', '');
  cellEls.forEach((row, r) => row.forEach((el, i) => {
    if (!el) return;
    const w = restoreWidths[r]?.[i];
    setStyle(el, 'width', isCellWidth(w) ? `${w}px` : '');
    setStyle(el, 'minWidth', '');
  }));
}

/**
 * 표 정렬(2026-10-06 오너 결정 — 표 위쪽 가장자리의 떠 있는 단추 셋, TableAlignFloat).
 * 값은 표 노드의 정렬 칸(format)에 둔다. Lexical 이 모든 블록에 원래 저장하는 칸이라 저장 형식이 바뀌지 않는다.
 * 그릴 때는 좌우 여백 auto 로 표 상자를 옮긴다(Lexical 이 표에 거는 text-align 은 칸 글자에만 닿는데,
 * 칸은 테마가 text-align:start 를 걸어 막고 있다). 왼쪽(기본)은 여백을 비운다 — 정렬을 정하지 않은 표는
 * 종전과 한 글자도 달라지지 않는다. 꽉 찬 표에서는 여백이 생기지 않아 보이는 변화가 없다.
 */
export type TableAlign = 'left' | 'center' | 'right';

export function tableAlignOf(format: string | null | undefined): TableAlign {
  if (format === 'center') return 'center';
  if (format === 'right' || format === 'end') return 'right';
  return 'left';
}

export function applyTableAlign(tableEl: HTMLElement, align: TableAlign): void {
  setStyle(tableEl, 'marginLeft', align === 'left' ? '' : 'auto');
  setStyle(tableEl, 'marginRight', align === 'center' ? 'auto' : align === 'right' ? '0px' : '');
}

/** 표를 담은 칸의 글자 폭(px) — 표의 % 폭이 이것에 대한 비율이다. 안쪽 여백을 뺀다 */
export function containerContentWidth(tableEl: HTMLElement): number {
  const parent = tableEl.parentElement;
  if (!parent) return 0;
  const style = window.getComputedStyle(parent);
  return parent.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
}

/** 표 DOM 의 칸을 줄별로 — 편집기는 table > tr, 내보낸 HTML 은 table > tbody > tr 다 */
export function tableCellElements(tableEl: HTMLElement): HTMLElement[][] {
  const rows: HTMLElement[] = [];
  Array.from(tableEl.children).forEach((child) => {
    if (child.tagName === 'TR') rows.push(child as HTMLElement);
    else if (child.tagName === 'TBODY' || child.tagName === 'THEAD' || child.tagName === 'TFOOT') {
      Array.from(child.children).forEach((tr) => { if (tr.tagName === 'TR') rows.push(tr as HTMLElement); });
    }
  });
  return rows.map((tr) => Array.from(tr.children)
    .filter((c) => c.tagName === 'TD' || c.tagName === 'TH') as HTMLElement[]);
}
